/**
 * @fileoverview Main entry point for the Salla Embedded SDK.
 *
 * This SDK provides a communication bridge between embedded third-party apps
 * and the Salla Dashboard host.
 *
 * @example Using window global
 * ```typescript
 * await salla.embedded.init({ debug: true });
 * salla.embedded.ui.loading.hide();
 * ```
 *
 * @example Using ES module import
 * ```typescript
 * import { embedded } from '@salla.sa/embedded-sdk';
 * await embedded.init({ debug: true });
 * ```
 */

import { getEmbeddedApp, EmbeddedApp } from "./core/EmbeddedApp";
import { SDK_VERSION } from "./core/constants";

// Re-export types
export type {
  InitOptions,
  EmbeddedState,
  ExtendedAction,
  ToastType,
  ConfirmOptions,
  ConfirmResult,
  ConfirmVariant,
} from "./core/types";

// Re-export validation types for advanced use cases
export type { ValidationResult } from "./core/validation";

export type { AuthModule } from "./modules/auth";
export type { PageModule, NavToOptions } from "./modules/page";
export type {
  NavModule,
  PrimaryActionConfig,
  NavItemInput,
  NavItemNodeInput,
  NavItemChildInput,
} from "./modules/nav";
export type { UIModule, LoadingMode, ToastOptions } from "./modules/ui";
export type {
  CheckoutModule,
  CheckoutCreateConfig,
  CheckoutResult,
  CheckoutResultCallback,
  CheckoutItem,
  CheckoutByIdInput,
  CheckoutInputError,
} from "./modules/checkout";
export { INVALID_CHECKOUT_INPUT } from "./modules/checkout";

// Export the singleton instance
export const embedded = getEmbeddedApp();

// Export version and events
export const version = SDK_VERSION;

// Export class for advanced use cases
export { EmbeddedApp };

// Re-export getEmbeddedApp for advanced use cases
export { getEmbeddedApp, resetEmbeddedApp } from "./core/EmbeddedApp";

// ============================================================================
// Window Global Setup
// ============================================================================

/**
 * Salla interface extension for the embedded SDK.
 */
interface SallaEmbedded {
  embedded: EmbeddedApp;
}

declare global {
  interface Window {
    salla: SallaEmbedded & Record<string, unknown>;
    Salla: SallaEmbedded & Record<string, unknown>;
  }
}

/**
 * Set up window globals.
 * Supports both window.salla.embedded and window.Salla.embedded
 */
if (typeof window !== "undefined") {
  // Initialize salla object if it doesn't exist
  window.salla = window.salla || window.Salla || {};
  window.Salla = window.salla;

  // Attach embedded SDK
  if (!window.salla.embedded) {
    window.salla.embedded = embedded;
  }
  if (!window.Salla.embedded) {
    window.Salla.embedded = embedded;
  }
}
